<script setup lang="ts">
import {computed, inject, ref, shallowRef, watch} from "vue";
import {UIMenuGroup, UIMenuItem} from "@/utils/UIConstants/UIMenuGroup.ts";
import Translator from "@/utils/common/Translator.ts";
import SideBarUtils from "@/utils/common/SideBarUtils.ts";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import {sideBarContext} from "@/utils/presenters/interfaces.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import {refDebounced} from "@vueuse/core";
import ListViewToolbarUtils from "@/utils/common/ListViewToolbarUtils.ts";
import SecurityUtils from "@/utils/login/SecurityUtils.ts";
import router from "@/router";
import {useRoute} from "vue-router";
import BlockUtils from "@/utils/common/BlockUtils.ts";
import GUIUtilities from "@/utils/common/GUIUtilities.ts";

const context = inject(sideBarContext, {
  searchValue: ref(),
  panel: ref("home"),
  displayedMenuGroups: computed(() => []),
  searchMenuGroups: computed(() => []),
  currentGroup: ref(null),
  currentSubGroup: ref(null)
});
const {darkMode, showSidebarMenuIconColors} = storeToRefs(useNamaFlagsStore());
const debouncedSearchValue = refDebounced(context.searchValue, 200);
const menuGroupsToDisplay = shallowRef<UIMenuGroup[]>([]);
const expandedGroups = ref<Record<string, boolean>>({});
const expandedSubGroups = ref<Record<string, boolean>>({});
const currentItemKey = ref<string | null | undefined>(null);
const route = useRoute();

function isCurrentItem(item: UIMenuItem): boolean {
  return currentItemKey.value === (item.code || item.id);
}

function updateMenuGroups() {
  let groups: UIMenuGroup[];
  if (ObjectChecker.isNotEmptyOrNull(debouncedSearchValue.value)) {
    groups = context.searchMenuGroups.value;
  } else {
    const map = new Map<string, UIMenuGroup>();
    context.displayedMenuGroups.value.forEach(g => {
      if (g.groupData.id)
        map.set(g.groupData.id, g)
    });
    groups = Array.from(map.values());
  }

  const currentIds = menuGroupsToDisplay.value.map(g => g.groupData.id).join(',');
  const newIds = groups.map(g => g.groupData.id).join(',');

  if (currentIds !== newIds) {
    menuGroupsToDisplay.value = groups;
  }
}

watch(
  [debouncedSearchValue, () => context.searchMenuGroups.value, () => context.displayedMenuGroups.value],
  updateMenuGroups,
  {immediate: true}
);

watch(debouncedSearchValue, (searchValue) => {
  if (ObjectChecker.isNotEmptyOrNull(searchValue)) {
    for (const group of menuGroupsToDisplay.value) {
      if (group.groupData.code)
        expandedGroups.value[group.groupData.code] = true;
      for (const subGroup of group.subGroups) {
        if (subGroup.subGroupData.code)
          expandedSubGroups.value[subGroup.subGroupData.code] = true;
      }
    }
  } else {
    expandedGroups.value = {};
    expandedSubGroups.value = {};
    expandToCurrentItem();
  }
});

function expandToCurrentItem() {
  for (const group of menuGroupsToDisplay.value) {
    for (const subGroup of group.subGroups) {
      for (const item of subGroup.subItems) {
        if (context.isCurrent && context.isCurrent(item)) {
          if (group.groupData.code)
            expandedGroups.value[group.groupData.code] = true;
          if (subGroup.subGroupData.code)
            expandedSubGroups.value[subGroup.subGroupData.code] = true;
          currentItemKey.value = item.code || item.id;
          return;
        }
      }
    }
  }
  currentItemKey.value = null;
}

watch(
  [menuGroupsToDisplay, () => context.currentGroup?.value],
  () => {
    if (menuGroupsToDisplay.value.length > 0) {
      expandToCurrentItem();
    }
  },
  {immediate: true}
);
function scrollToTarget() {
  const activeItem = document.querySelector(".is-current-item");
  if (!activeItem)
    return;
  GUIUtilities.scrollIntoView(activeItem);
}
watch(route,()=>{
  expandToCurrentItem();
  setTimeout(()=>{
    scrollToTarget();
  },BlockUtils.qExpansionDuration)
})

function onItemClick(item: UIMenuItem, e?: Event | MouseEvent) {
  let openInNewTab: boolean = SideBarUtils.shouldOpenInNewTab(e);
  currentItemKey.value = item.code || item.id;
  if (context.goToEntity)
    context.goToEntity(item, openInNewTab);
}

async function addEntity(item: UIMenuItem, e: Event | MouseEvent) {
  let openInNewTab = SideBarUtils.shouldOpenInNewTab(e);
  if (!SecurityUtils.userCanCreate(item.target?.targetEntity ?? ''))
    return;
  await ListViewToolbarUtils.onNewRecord(item.place?.query, openInNewTab);
}

function translateGroup(group: namaTypes.DTOMenuGroup) {
  return Translator.translateMenuGroup(group);
}

function translateItem(item: UIMenuItem) {
  return SideBarUtils.translateMenuItem(item);
}
</script>

<template>
  <div class="tw-w-full tw-h-full tw-flex tw-flex-col tw-text-namaFontColor-menu-light dark:tw-text-namaFontColor-menu-dark dark:tw-bg-namaBackground-deep-dark">
    <slot/>
    <q-scroll-area :dark="darkMode" class="fit tw-flex-1">
      <q-list :dark="darkMode" class="tw-px-0">
        <!-- Groups (Level 1) -->
        <q-expansion-item
          v-for="group in menuGroupsToDisplay"
          :key="group.groupData.code"
          v-model="expandedGroups[group.groupData.code??'']"
          :dark="darkMode"
          class="tw-px-0"
          expand-icon-class="tw-text-inherit"
          :header-class="['tw-border-b tw-border-black/80 dark:tw-border-white/20', group.groupData.code && expandedGroups[group.groupData.code] ? 'tw-bg-black/5 dark:tw-bg-white/10' : '']"
        >
          <template #header>
            <q-item-section avatar class="tw-min-w-0 tw-p-0">
              <NamaIcon
                :colored="showSidebarMenuIconColors"
                :menu-item="group.groupData"
                :default-icon="group.groupData.iconCode || 'nm-folders'"
                class="tw-size-7"
              />
            </q-item-section>
            <q-item-section class="tw-text-base tw-font-medium tw-p-0">
              {{ translateGroup(group.groupData) }}
            </q-item-section>
          </template>

          <template v-if="group.groupData.code && expandedGroups[group.groupData.code]">
            <q-expansion-item
              v-for="subGroup in group.subGroups"
              :key="subGroup.subGroupData.code"
              v-model="expandedSubGroups[subGroup.subGroupData.code ?? '']"
              :dark="darkMode"
              expand-icon-class="tw-text-inherit"
              :header-class="['tw-px-6 tw-my-1', subGroup.subGroupData.code && expandedSubGroups[subGroup.subGroupData.code] ? 'tw-bg-black/5 dark:tw-bg-white/5' : '']">
              <template #header>
                <q-item-section avatar class="tw-min-w-0">
                  <NamaIcon
                    :colored="showSidebarMenuIconColors"
                    :menu-item="subGroup.subGroupData"
                    :default-icon="subGroup.subGroupData.iconCode || 'nm-folder'"
                    class="tw-size-[24px] tw-text-[24px]"
                  />
                </q-item-section>
                <q-item-section class="tw-text-[15px]">
                  {{ translateGroup(subGroup.subGroupData) }}
                </q-item-section>
              </template>

              <template v-if="subGroup.subGroupData.code && expandedSubGroups[subGroup.subGroupData.code]">
                <div
                  v-for="item in subGroup.subItems"
                  :key="item.code || item.id"
                  class="tw-flex tw-my-1 tw-w-full tw-px-6 tw-gap-1 tw-items-center tw-justify-between">
                  <q-item
                    clickable
                    v-ripple
                    dense
                    :dark="darkMode"
                    class="tw-rounded-md tw-px-3 tw-grow"
                    :class="{'tw-bg-primary-light dark:tw-bg-primary-dark tw-text-namaFontColor-menu-dark is-current-item': isCurrentItem(item)}"
                    @click.stop="(event)=>onItemClick(item,event)">
                    <q-item-section avatar class="tw-min-w-0">
                      <NamaIcon
                        :colored="showSidebarMenuIconColors"
                        :menu-item="item"
                        :default-icon="item.iconCode || 'nm-monitoring'"
                        class="tw-size-[22px] tw-text-[22px]"/>
                    </q-item-section>
                    <q-item-section class="tw-text-[14px]">
                      {{ translateItem(item) }}
                    </q-item-section>
                  </q-item>
                  <a
                    v-if="SecurityUtils.userCanCreate(item.target?.targetEntity ?? '')"
                    @click.stop="(event) => addEntity(item, event)"
                    class="hover:tw-bg-gray-200 dark:hover:tw-bg-white/20 tw-p-1.5 tw-rounded-lg tw-cursor-pointer">
                    <NamaIcon class="tw-size-[20px] active-primary" default-icon="nm-add-record"/>
                  </a>
                </div>
              </template>
            </q-expansion-item>
          </template>
        </q-expansion-item>
      </q-list>
    </q-scroll-area>
  </div>
</template>
